import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { Flex, Link, QueryLoader, Tab, Tabs, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { useAppContext } from "~/contexts/appContext";
import { useGetArenaQuery } from "~/graphql/mainHooks";
import { DataStructureRoutingProvider } from "~/pages/Reporting/DataStructureRoutingContext";
import {
  DataStructure,
  DataStructureSidebar,
  type DataStructureTabId,
} from "~/pages/Reporting/Settings/components/DataStructure";
import { useDataStructureRouting } from "~/pages/Reporting/useDataStructureRouting";
import { SPACING } from "~/styles/variables";
import { toGlobalId } from "~/utils/graphql-helpers";
import { ReportingMissingPermissionState } from "../components/MissingPermissionState";
import { isUnauthorizedError } from "../utils";
import { CustomTemplatesList } from "./components/CustomTemplatesList";
import { useFetchTemplatesByArena } from "./hooks";
import * as styles from "./styles_vanilla.css";

type ArenaDetailParams = {
  arenaId: string;
};

type TabId = "custom-templates" | "data-structures";

const ARENA_DATA_STRUCTURE_TABS: readonly DataStructureTabId[] = ["gl", "gpx"];

export const ArenaDetail = () => {
  const { arenaId } = useParams<ArenaDetailParams>();
  const { user } = useAppContext();

  const routing = useDataStructureRouting({
    dataStructureBasePath: `/reporting/${arenaId}/data-structures`,
    parentPath: `/reporting/${arenaId}`,
    validTabs: ARENA_DATA_STRUCTURE_TABS,
  });

  const activeTab: TabId = routing.isDataStructureActive
    ? "data-structures"
    : "custom-templates";

  const arenaIdNum = Number(arenaId);
  if (!arenaId || Number.isNaN(arenaIdNum)) {
    throw new Error("Invalid arenaId URL parameter");
  }
  const { templates, queryResult } = useFetchTemplatesByArena(arenaIdNum);

  const { data: arenaData } = useGetArenaQuery({
    context: withClientContext("staff"),
    variables: { id: toGlobalId("ArenaObjectType", arenaIdNum) },
  });

  const arenaName = arenaData?.arena?.name || "";

  const handleOpenWorkspace = useCallback(() => {
    if (!user.id || !arenaId) {
      toast.error("There was an error navigating to the arena");
      return;
    }
    const url = `/session_exchange/initiate/${user.id}?arena_id=${arenaId}&sess_ex_type=ADMIN_TO_STAFF&redirect_url=/dashboard`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [user.id, arenaId]);

  if (queryResult.error && isUnauthorizedError(queryResult.error)) {
    return <ReportingMissingPermissionState />;
  }

  const handleTabChange = (tab: TabId) => {
    if (tab === activeTab) return;
    if (tab === "data-structures") {
      routing.navigateToDataStructures();
    } else {
      routing.navigateToParent();
    }
  };

  const renderTabContent = () => {
    if (activeTab === "data-structures") {
      return <DataStructure arenaId={arenaIdNum} arenaName={arenaName} />;
    }

    return (
      <Flex
        flexDirection="column"
        flexGrow={1}
        gap={SPACING.MEDIUM}
        className={styles.templatesWrapper}
      >
        <CustomTemplatesList arenaId={arenaIdNum} templates={templates} />
      </Flex>
    );
  };

  return (
    <Flex flexDirection="column" className={styles.container}>
      <Flex flexDirection="column" className={styles.header}>
        <Link to="/reporting" icon="arrow-left" variant="primary" className={styles.backLink}>
          All clients
        </Link>
        <Flex alignItems="center" gap={8} className={styles.arenaNameContainer}>
          <Text variant="h2">{arenaName}</Text>
          {arenaId && (
            <Icon
              type="arrow-up-right-from-square"
              size="small"
              color={Colors.Primary600}
              onClick={handleOpenWorkspace}
              role="button"
              tabIndex={0}
              aria-label="Open workspace in new tab"
              className={styles.openWorkspaceIcon}
            />
          )}
        </Flex>
        <Tabs hasBottomBorder={false} hasPaddingInline={false}>
          <Tab
            text="Templates"
            active={activeTab === "custom-templates"}
            onClick={() => handleTabChange("custom-templates")}
          />
          <Tab
            text="Data structures"
            active={activeTab === "data-structures"}
            onClick={() => handleTabChange("data-structures")}
          />
        </Tabs>
      </Flex>
      <QueryLoader result={queryResult}>
        <DataStructureRoutingProvider value={routing}>
          <Flex flexGrow={1} flexDirection="row">
            {activeTab === "data-structures" && (
              <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.sidebar}>
                <DataStructureSidebar
                  activeTab={routing.dataStructureType}
                  onTabChange={routing.handleTypeChange}
                  visibleTabs={ARENA_DATA_STRUCTURE_TABS}
                />
              </Flex>
            )}
            <Flex flexGrow={1} flexDirection="column">
              {renderTabContent()}
            </Flex>
          </Flex>
        </DataStructureRoutingProvider>
      </QueryLoader>
    </Flex>
  );
};
